'use client';

import { useClickAway } from 'ahooks';
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BaseLineDown } from '@/app/components/icons/BaseLineDown';
import { cn } from '@/app/utils';

export interface ProductSelectOption {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
}

export interface ProductSelectProps {
  value: string;
  options: ProductSelectOption[];
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * 产品页下拉选择组件
 * 自实现，不依赖 Radix，避免打开时 scroll lock 导致页面滚动条消失
 */
export const ProductSelect: FC<ProductSelectProps> = ({
  value,
  options,
  onValueChange,
  disabled = false,
  placeholder,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ bottom: 0, left: 0, width: 0 });

  useClickAway(
    () => {
      setOpen(false);
    },
    [containerRef, dropdownRef],
    'mousedown',
  );

  const currentOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({
      bottom: window.innerHeight - rect.top + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [open]);

  // 滚动时自动收起下拉
  useEffect(() => {
    if (!open) return;
    const handleScroll = () => setOpen(false);
    window.addEventListener('scroll', handleScroll, { capture: true });
    return () => window.removeEventListener('scroll', handleScroll, { capture: true });
  }, [open]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onValueChange(optionValue);
      setOpen(false);
    },
    [onValueChange],
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  return (
    <div ref={containerRef} className="relative w-fit">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'group flex h-9 w-full min-w-[170px] items-center justify-between gap-2 rounded-10 border-none bg-system-white/5 px-2 text-system-white transition-colors hover:bg-system-white/10 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={placeholder}
      >
        <span className="flex flex-1 items-center gap-1 truncate">
          {currentOption ? (
            <>
              {currentOption.icon}
              <span className="text-system-white text-xs">{currentOption.label}</span>
            </>
          ) : (
            <span className="text-system-white text-xs">{placeholder}</span>
          )}
        </span>
        <BaseLineDown
          className={cn('size-4 shrink-0 text-system-white64 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            data-state={open ? 'open' : 'closed'}
            className={cn(
              'fixed z-[9999] flex flex-col gap-1 rounded-8 border border-system-white02 border-solid bg-system-white/5 p-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.32)] backdrop-blur-[100px]',
              'origin-bottom animate-scaleYIn',
            )}
            style={{
              bottom: dropdownPosition.bottom,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
            onKeyDown={handleKeyDown}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-selected={option.value === value}
                onClick={() => handleSelect(option.value)}
                className={cn(
                  'flex h-10 w-full cursor-pointer items-center gap-1 rounded-6 px-2 py-1.5 text-left text-sm transition-colors duration-150 hover:bg-system-white/10',
                  option.value === value && 'bg-system-white/10',
                )}
              >
                {option.icon}
                <span className="text-system-white text-xs">{option.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};
